import {makeStyles} from "@material-ui/styles";
import {Theme} from "@material-ui/core/styles";

export const aircraftFormStyles = makeStyles((theme: Theme) => ({
  formWrapper: {
    '& form': {
      display: 'flex',
      flexDirection: 'column',
      padding: theme.spacing(0, 1)
    }
  },
  formHeader: {
    color: theme.palette.secondary.dark,
    paddingLeft: theme.spacing(1.5)
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    '& > div': {
      flex: 1,
      margin: theme.spacing(0, .5)
    },
    padding: theme.spacing(2, 0)
  },
  sectionTitle: {
    color: '#0D3B66',
    fontSize: '14px',
    fontWeight: 600,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    paddingLeft: theme.spacing(0.5),
  },
  mappingRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0),
    '& > div': {
      flex: 1,
    }
  },
  chipContainer: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: theme.spacing(0.5),
    padding: theme.spacing(1, 0),
  }
}));

export const aircraftDetailStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: theme.spacing(2),
  },
  detailCard: {
    background: '#fff',
    borderRadius: 8,
    padding: theme.spacing(3),
    boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
    marginBottom: theme.spacing(2),
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  infoItem: {
    '& label': {
      display: 'block',
      fontSize: 12,
      color: '#888',
      marginBottom: 4,
    },
    '& span': {
      display: 'block',
      fontSize: 16,
      fontWeight: 500,
      color: '#333',
    }
  },
  /* ---- Airplane fuselage ---- */
  fuselageWrapper: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(2, 0),
  },
  fuselage: {
    position: 'relative' as const,
    background: 'linear-gradient(180deg, #f8f9fb 0%, #eef1f5 100%)',
    border: '2px solid #c5cdd8',
    borderRadius: '50% 50% 8px 8px / 12% 12% 0% 0%',
    padding: '60px 24px 32px',
    maxWidth: 500,
    width: '100%',
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
  },
  noseOverlay: {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    right: 0,
    height: 56,
    background: 'linear-gradient(180deg, #dce3ec 0%, transparent 100%)',
    borderRadius: '50% 50% 0 0 / 100% 100% 0 0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  cockpitIcon: {
    marginTop: 4,
    fontSize: 22,
    color: '#8899aa',
  },
  tailSection: {
    position: 'relative' as const,
    marginTop: 4,
    paddingTop: 8,
    borderTop: '2px dashed #d0d7e0',
    textAlign: 'center' as const,
    color: '#aab4c1',
    fontSize: 11,
    letterSpacing: 1,
    textTransform: 'uppercase' as const,
  },
  wingIndicator: {
    position: 'absolute' as const,
    top: 0,
    width: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#b0bcc8',
    fontSize: 18,
  },
  wingLeft: {
    left: -36,
  },
  wingRight: {
    right: -36,
  },
  exitLabel: {
    position: 'absolute' as const,
    fontSize: 9,
    fontWeight: 700,
    color: '#e53935',
    letterSpacing: 0.5,
    textTransform: 'uppercase' as const,
    background: '#fff3f3',
    border: '1px solid #ffcdd2',
    borderRadius: 3,
    padding: '1px 5px',
  },
  exitLeft: {
    left: -4,
  },
  exitRight: {
    right: -4,
  },
  /* ---- Column headers ---- */
  colHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 6,
  },
  colHeaderLabel: {
    fontWeight: 700,
    fontSize: 12,
    color: '#7b8a9a',
    textAlign: 'center' as const,
  },
  /* ---- Seat row ---- */
  seatRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 3,
    position: 'relative' as const,
  },
  rowLabel: {
    width: 28,
    textAlign: 'center' as const,
    fontSize: 11,
    fontWeight: 600,
    color: '#8899aa',
    flexShrink: 0,
  },
  aisleGap: {
    width: 24,
    flexShrink: 0,
  },
  /* ---- Individual seat ---- */
  seat: {
    width: 34,
    height: 34,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 6,
    fontSize: 10,
    fontWeight: 600,
    margin: '0 2px',
    cursor: 'default',
    border: '1.5px solid transparent',
    transition: 'all 0.15s ease',
    position: 'relative' as const,
    '&:hover': {
      transform: 'scale(1.12)',
      zIndex: 2,
      boxShadow: '0 3px 10px rgba(0,0,0,0.18)',
    },
  },
  seatBlocked: {
    backgroundColor: '#eceff1',
    color: '#b0bec5',
    borderColor: '#cfd8dc',
    '&:hover': {
      transform: 'none',
      boxShadow: 'none',
    },
  },
  /* ---- Legend ---- */
  legendContainer: {
    display: 'flex',
    gap: theme.spacing(3),
    padding: theme.spacing(1, 0),
    flexWrap: 'wrap' as const,
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    fontSize: 13,
    color: '#555',
  },
  legendSwatch: {
    width: 18,
    height: 18,
    borderRadius: 5,
    border: '1.5px solid #ddd',
  },
  /* ---- Separator ---- */
  classDivider: {
    height: 2,
    background: 'linear-gradient(90deg, transparent, #c5cdd8, transparent)',
    margin: '8px 0',
    borderRadius: 1,
  },
  seatMapContainer: {
    overflowX: 'auto' as const,
    padding: theme.spacing(2, 0),
  },
}));
